"use client";

import { useState, useRef, useEffect } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import Chip from "@/components/global/ui/Chip";
import { CircleSlider } from "./CircleSlider";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import Image from "next/image";
import { GROQImageLoader } from "@/sanity/lib/definitions";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import orangeBG from "public/home/orange-bg-strategy.png";

import orangeLogo from "public/icons/orange-logo.svg";
import whiteLogo from "public/icons/white-logo.svg";

const EASE = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TEXT = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_CTA = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...EASE] };
const TEXT_TRANSITION = { duration: 0.8, ease: [...EASE], delay: 0.08 };
const CTA_TRANSITION = { duration: 0.8, ease: [...EASE], delay: 0.22 };
const DRAG_CURSOR_INITIAL = { opacity: 0, scale: 0.7 } as const;
const DRAG_CURSOR_ANIMATE = { opacity: 1, scale: 1 } as const;
const DRAG_CURSOR_EXIT = { opacity: 0, scale: 0.7 } as const;
const DRAG_CURSOR_TRANSITION = { duration: 0.2 };

interface Props {
  data: PageHomeSchema;
}

type Slide = NonNullable<PageHomeSchema["strategyTrackingSlides"]>[number];

function renderSlide(slide: Slide | undefined) {
  const isLight = slide?.theme === "light" || slide?.theme === "light-gradient";
  const hasUpdatedLayout = !!slide?.updatedLayout;
  const cardBg = (() => {
    if (slide?.theme === "light-gradient")
      return {
        background: "linear-gradient(180deg, #FFB19B 50%, #ffffff 100%)",
        border: "0.125rem solid #ffb19b",
      };
    if (slide?.theme === "light")
      return {
        background: "#ffffff",
        ...(hasUpdatedLayout ? {} : { border: "0.125rem solid #e0e0e0" }),
      };
    return { background: "#1b1b1b" };
  })();
  const textColors = !isLight
    ? "prose-p:text-white/70 prose-headings:text-white"
    : hasUpdatedLayout
      ? "prose-p:text-black/80 prose-headings:text-black/80"
      : "prose-p:text-black/90 prose-headings:text-black";

  return (
    <div
      className="relative flex min-h-[55rem] flex-col items-center overflow-hidden rounded-[2rem] px-[2.5rem] py-[3rem] !pt-[3rem] transition-all duration-500 sm:min-h-[74rem] sm:px-[4rem] sm:py-[6rem]"
      style={cardBg}>
      <div className="flex max-w-[40.5rem] flex-col items-center gap-[3rem]">
        <div
          className={`flex flex-col items-center text-center w-fit px-[1.5rem] py-[1rem]${hasUpdatedLayout ? " mb-[2rem]" : ""}`}
          style={{
            background: isLight ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.15)",
          }}>
          <p
            className={`mb-0 leading-none text-center uppercase font-spacemono text-[1.75rem]${hasUpdatedLayout ? " [text-box:trim-both_cap_alphabetic]" : ""}`}
            style={{
              color: isLight ? "#040404" : "#ffffff",
            }}>
            Special features
          </p>
        </div>

        {slide?.icon?.color === "orange" && (
          <Image
            src={orangeLogo}
            alt=""
            className={hasUpdatedLayout ? "w-[3.75rem] h-[4rem]" : undefined}
          />
        )}

        {slide?.icon?.color === "white" && (
          <Image
            src={whiteLogo}
            alt=""
            className={`opacity-${slide?.icon?.opacity}`}
          />
        )}

        {slide?.text && (
          <div
            className={`text-center prose-headings:text-[4rem] sm:prose-headings:text-[6rem] prose-headings:font-helveticaNeue prose-headings:font-[500]
            ${textColors}
            ${hasUpdatedLayout ? "flex flex-col gap-[3rem] prose-p:[text-box:trim-both_cap_alphabetic] prose-headings:m-0 prose-headings:tracking-normal prose-headings:[text-box:trim-both_cap_alphabetic]" : ""}
              `}>
            <PortableText
              value={slide.text}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      {slide?.image?.src && (
        <div
          className={
            slide?.imageAbsolute
              ? `absolute bottom-[-5rem] w-full left-1/2 -translate-x-1/2${hasUpdatedLayout ? " sm:bottom-0" : ""}`
              : "w-full"
          }>
          <Image
            src={slide.image.src}
            alt=""
            width={slide.image.width}
            height={slide.image.height}
            className="w-full object-cover"
            loader={GROQImageLoader}
            placeholder={slide.image.lqip ? "blur" : "empty"}
            blurDataURL={slide.image.lqip}
          />
        </div>
      )}
    </div>
  );
}

function StrategyTrackingSectionDesktop({ data }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const slides = data?.strategyTrackingSlides ?? [];
  const count = slides.length;

  const sliderRef = useRef<HTMLDivElement>(null);
  const [isCursorInside, setIsCursorInside] = useState(false);
  const insideRef = useRef(false);

  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 200, mass: 0.5 };
  const navX = useSpring(cursorX, springConfig);
  const navY = useSpring(cursorY, springConfig);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;

    const updatePosition = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (!insideRef.current) {
        cursorX.jump(x);
        cursorY.jump(y);
        navX.jump(x);
        navY.jump(y);
        insideRef.current = true;
        setIsCursorInside(true);
      } else {
        cursorX.set(x);
        cursorY.set(y);
      }
    };

    const onPointerEnter = (e: PointerEvent) => {
      updatePosition(e);
    };

    const onPointerLeave = () => {
      insideRef.current = false;
      setIsCursorInside(false);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!insideRef.current) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      cursorX.set(x);
      cursorY.set(y);
    };

    el.addEventListener("pointerenter", onPointerEnter);
    el.addEventListener("pointerleave", onPointerLeave);
    document.addEventListener("pointermove", onPointerMove);

    return () => {
      el.removeEventListener("pointerenter", onPointerEnter);
      el.removeEventListener("pointerleave", onPointerLeave);
      document.removeEventListener("pointermove", onPointerMove);
    };
  }, [cursorX, cursorY, navX, navY]);

  return (
    <section className="relative z-[4] hidden cursor-none overflow-clip bg-[#040404] lg:block">
      <div className="absolute bottom-0 z-[9] h-[10%] w-full bg-gradient-to-t from-white to-transparent" />
      <div className="absolute top-0 z-[9] h-[10%] w-full bg-gradient-to-b from-[#040404] to-transparent" />
      {/*
       * Figma 3901:5299 "Orange BG": a 2137×1159 rect (111.3% of 1920) whose
       * radial gradient is centred on its top edge, 235px below the section
       * top, with a 60px layer blur. The vertical radius is fixed to the Figma
       * 1159px (144.875rem) so white lands at the same point regardless of
       * the section height. The horizontal radius is widened (250% instead of
       * Figma's 147%) so the black→orange fade reads as a full-width band
       * instead of a dark dome with orange corners; the mask feathers the
       * rect's top edge the way the blur does.
       */}
      <div
        className="pointer-events-none absolute top-[21.875rem] bottom-0 left-1/2 w-[111.3%] -translate-x-1/2"
        style={{
          background: `radial-gradient(250% 144.875rem at 50% 7.5rem, #050200 20%, #F50 50%, #FFF 100%)`,
          maskImage: "linear-gradient(to bottom, transparent 0, #000 15rem)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0, #000 15rem)",
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-[2] bg-[url('/noise.svg?v=4')] [mask-image:linear-gradient(to_bottom,black_50%,transparent_95%)] bg-repeat opacity-50" />

      {/* Cards end 427px above the section bottom in Figma; the slider block already carries 191px of that */}
      <div className="relative z-10 pt-[12rem] pb-[29.5rem]">
        <div className="flex flex-col items-center px-[4rem] text-center">
          {data?.strategyTrackingChip && (
            <motion.div
              className="mb-[2.5rem]"
              {...REVEAL_CHIP}
              transition={CHIP_TRANSITION}>
              <Chip
                text={data.strategyTrackingChip}
                animatedIcon
                spinnerSize={32}
                spinnerVariant="wave"
                className="[&_p]:!text-white prose-p:!text-white"
              />
            </motion.div>
          )}

          {data?.strategyTrackingText && (
            <motion.div
              className="mb-[4rem] max-w-[76.125rem] text-white prose-h2:text-[8rem] prose-h2:tracking-normal prose-p:text-[2.25rem] prose-p:text-white/70"
              {...REVEAL_TEXT}
              transition={TEXT_TRANSITION}>
              <PortableText
                value={data.strategyTrackingText}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.strategyTrackingCTA && (
            <motion.div
              {...REVEAL_CTA}
              transition={CTA_TRANSITION}>
              <CTAButton data={data?.strategyTrackingCTA} />
            </motion.div>
          )}
        </div>

        <div
          ref={sliderRef}
          className="relative mt-[6rem]">
          <CircleSlider
            className="overflow-x-clip overflow-y-visible"
            count={count}
            externalIndex={activeIndex}
            onActiveChange={setActiveIndex}
            cardWidthRem={59}
            angleStepDeg={30}
            cardGapRem={30}>
            {(index) => renderSlide(slides[index])}
          </CircleSlider>

          <AnimatePresence>
            {isCursorInside && (
              <motion.div
                className="pointer-events-none z-20 flex w-fit items-center justify-center gap-[0.125rem] select-none"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  x: navX,
                  y: navY,
                  translateX: "-50%",
                  translateY: "-50%",
                }}
                initial={DRAG_CURSOR_INITIAL}
                animate={DRAG_CURSOR_ANIMATE}
                exit={DRAG_CURSOR_EXIT}
                transition={DRAG_CURSOR_TRANSITION}>
                <div className="flex h-[4rem] w-[4rem] items-center justify-center border border-white/25 bg-black text-white">
                  <svg
                    className="h-[2rem] w-[2rem] flex-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 16 16"
                    aria-hidden="true">
                    <path
                      fill="#fff"
                      fillOpacity="0.01"
                      d="M0 0h16v16H0z"
                      style={{ mixBlendMode: "multiply" }}
                      transform="matrix(-1 0 0 1 16 0)"></path>
                    <path
                      fill="#fff"
                      d="m5.003 8 5 5 .7-.7-4.3-4.3 4.3-4.3-.7-.7z"></path>
                  </svg>
                </div>
                <div className="flex h-[4rem] items-center border-t border-b border-white/25 bg-black px-[1rem] font-spacemono text-[2.25rem] text-white uppercase">
                  Drag
                </div>
                <div className="flex h-[4rem] w-[4rem] items-center justify-center border border-white/25 bg-black text-white">
                  <svg
                    className="h-[2rem] w-[2rem] flex-shrink-0 rotate-180"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 16 16"
                    aria-hidden="true">
                    <path
                      fill="#fff"
                      fillOpacity="0.01"
                      d="M0 0h16v16H0z"
                      style={{ mixBlendMode: "multiply" }}
                      transform="matrix(-1 0 0 1 16 0)"></path>
                    <path
                      fill="#fff"
                      d="m5.003 8 5 5 .7-.7-4.3-4.3 4.3-4.3-.7-.7z"></path>
                  </svg>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function StrategyTrackingSectionMobile({ data }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const slides = data?.strategyTrackingSlides ?? [];
  const count = slides.length;

  return (
    <section className="relative z-[4] overflow-x-clip bg-[#040404] lg:hidden">
      <div className="absolute top-0 z-[9] h-[10%] w-full bg-gradient-to-b from-[#040404] to-transparent" />

      <div className="absolute z-[1] h-full w-full">
        <Image
          src={orangeBG}
          alt=""
          className="w-full object-cover"
        />
      </div>
      <div className="pointer-events-none absolute inset-0 z-[2] bg-[url('/noise.svg?v=4')] [mask-image:linear-gradient(to_bottom,black_50%,transparent_95%)] bg-repeat opacity-50" />

      <div className="relative z-10 py-[12rem]">
        <div className="flex flex-col items-center px-[4rem] text-center">
          {data?.strategyTrackingChip && (
            <div className="mb-[2.5rem] [&_p]:!whitespace-nowrap">
              <Chip
                text={data.strategyTrackingChip}
                spinnerSize={32}
                spinnerVariant="wave"
                className="[&_p]:!text-white prose-p:!text-white"
              />
            </div>
          )}

          {data?.strategyTrackingText && (
            <div className="mb-[1rem] max-w-[76.125rem] text-white prose-h2:text-[5rem] prose-h2:tracking-normal prose-p:text-[2.25rem] prose-p:text-white/70">
              <PortableText
                value={data.strategyTrackingText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.strategyTrackingCTA && (
            <div>
              <CTAButton data={data?.strategyTrackingCTA} />
            </div>
          )}
        </div>

        <div className="relative mt-[2rem]">
          <CircleSlider
            className="overflow-x-clip overflow-y-visible"
            count={count}
            externalIndex={activeIndex}
            onActiveChange={setActiveIndex}
            cardWidthRem={40}
            angleStepDeg={20}
            cardGapRem={10}>
            {(index) => renderSlide(slides[index])}
          </CircleSlider>
        </div>
      </div>
    </section>
  );
}

export default function StrategyTrackingSection({ data }: Props) {
  return (
    <>
      <StrategyTrackingSectionDesktop data={data} />
      <StrategyTrackingSectionMobile data={data} />
    </>
  );
}
